import {
  createContext,
  useContext,
  useMemo,
  type PropsWithChildren,
} from "react";
import {
  styled,
  ToggleButtonGroup,
  ToggleButton,
  toggleButtonClasses,
} from "@mui/material";
import type { SearchScope } from "../lib/types/types";

type Color = "primary" | "secondary";

type ToggleButtonGroupContextValue = {
  color: Color;
};

const ToggleButtonGroupContext = createContext<ToggleButtonGroupContextValue>({
  color: "primary",
});

const StyledToggleButton = styled(ToggleButton)(({ color }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "calc(var(--spacing)*2)",
  border: "none !important",
  textTransform: "none",
  color: `var(${color === "secondary" ? `--text-primary` : "--color-white"}) !important`,
  fontWeight: "400 !important",
  fontSize: "16px",
  letterSpacing: "0.15px",
  lineHeight: "22px",
  borderRadius: "var(--radius-2xl) !important",
  margin: "0 !important",
  padding: "calc(var(--spacing)*2) !important",
  width: "64px",
  [`&.${toggleButtonClasses.selected}`]: {
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "#0000000A",
    },
  },
  [`& svg:not([data-highlight])`]: {
    borderRadius: "var(--radius-2xl)",
    backgroundColor: `${color === "secondary" ? `var(--color-white)` : "#F2F2F22B"}`,
    color: `var(${color === "secondary" ? `--text-tertiary` : "--color-white"})`,
    transition: "background-color 0.3s, color 0.3s",
  },
  [`&.${toggleButtonClasses.selected} svg:not([data-highlight])`]: {
    backgroundColor: `var(${color === "secondary" ? `--color-surface-accent-strong` : "--color-white"})`,
    color: `var(${color === "secondary" ? `--color-white` : "--color-surface-accent-strong"})`,
  },
  [`& [data-highlight]`]: {
    opacity: "1",
    transition: "opacity 0.3s",
  },
  [`&.${toggleButtonClasses.selected} [data-highlight]`]: {
    opacity: "0",
  },
}));

type ScopeSwitcherButtonProps = PropsWithChildren<{
  value: SearchScope;
  isHighlighted?: boolean;
}>;

export const ScopeSwitcherButton = ({
  value,
  isHighlighted,
  children,
}: ScopeSwitcherButtonProps) => {
  const { color } = useContext(ToggleButtonGroupContext);

  return (
    <StyledToggleButton value={value} color={color}>
      {isHighlighted ? (
        <div
          data-highlight
          className="absolute inset-[8px] size-[48px] overflow-hidden rounded-2xl p-0.5"
          style={{
            mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            maskComposite: "exclude",
          }}
        >
          <div
            className="absolute -inset-2 animate-[spin_3s_linear_infinite]"
            style={{
              background:
                "linear-gradient(to bottom, #0F9 0%, #00CC7A 5%, transparent 75%)",
            }}
          />
        </div>
      ) : null}
      {children}
    </StyledToggleButton>
  );
};

type Props = PropsWithChildren<{
  value: SearchScope;
  onChange: (newValue: SearchScope | null) => void;
  color: Color;
  hidden?: boolean;
}>;

export const ScopeSwitcherBase = ({
  value,
  onChange,
  color,
  hidden,
  children,
}: Props) => {
  const contextValue = useMemo(() => ({ color }), [color]);

  return (
    <ToggleButtonGroupContext value={contextValue}>
      <ToggleButtonGroup
        color={color}
        value={value}
        exclusive
        onChange={(_, newValue: SearchScope | null) => {
          onChange(newValue);
        }}
        aria-label="Platform"
        sx={{
          display: "flex",
          gap: "calc(var(--spacing)*2)",
          visibility: hidden ? "hidden" : "visible",
        }}
      >
        {children}
      </ToggleButtonGroup>
    </ToggleButtonGroupContext>
  );
};
